/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import axios from "axios";
import fetchData from "../../functions/fetchData";
import updateData from "../../functions/updateData";
import Layout from "../../layout/Layout";
import ExportButtons from "../../components/ExportButtons";
import Modal from "../../components/Modal";
import { getHeaders } from "../../utility/getHeader";

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    cat_name: "",
    description: ""
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      await fetchData("categories", setCategories, "id", {});
    } catch (error) {
      setError("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cat_name.trim()) {
      setError("Please enter category name");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await axios.post("/insertdata/categories", formData, getHeaders());
      setSuccess("Category added successfully!");
      setFormData({ cat_name: "", description: "" });
      fetchCategories();
    } catch (err) {
      setError("Error adding category");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (category) => {
    setEditingItem(category);
    setEditFormData({
      cat_name: category.cat_name,
      description: category.description || ""
    });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.cat_name.trim()) {
      setError("Please enter category name");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const updatePayload = { ...editFormData, id: editingItem.id };
      await updateData("categories", updatePayload);
      setSuccess("Category updated successfully!");
      setShowEditModal(false);
      setEditingItem(null);
      setEditFormData({});
      fetchCategories();
    } catch (err) {
      setError("Error updating category");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (category) => {
    if (window.confirm(`Are you sure you want to delete "${category.cat_name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        setError("");
        const whereCondition = { id: category.id };
        await updateData("categories", {}, whereCondition, "DELETE");
        setSuccess("Category deleted successfully!");
        fetchCategories();
      } catch (err) {
        setError("Error deleting category");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Close modal
  const closeModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditFormData({});
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter((category) =>
    category.cat_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.id?.toString().includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="ledger-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Category Management</h4>
        </div>

        {error && (
          <div className="alert alert-danger alert-custom">
            <i className="zmdi zmdi-close-circle mr-2"></i>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-custom">
            <i className="zmdi zmdi-check-circle mr-2"></i>
            {success}
          </div>
        )}

        <div className="row">
          {/* Form Section */}
          <div className="col-md-4">
            <div className="form-section">
              <h6><i className="zmdi zmdi-plus-circle mr-2"></i> Add New Category</h6>
              <form onSubmit={handleSubmit} className="ledger-form">
                <div className="form-group">
                  <label className="control-label">Category Name *</label>
                  <input
                    type="text"
                    name="cat_name"
                    className="form-control"
                    value={formData.cat_name}
                    onChange={handleInputChange}
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter category description"
                    rows="3"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-submit btn-block"
                  disabled={loading}
                >
                  {loading && <span className="loading-spinner"></span>}
                  Add Category
                </button>
              </form>
            </div>
          </div>

          {/* Table Section */}
          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Existing Categories ({filteredCategories.length})</h6>
              </div>
              <div className="ledger-table-panel">
                <table className="table table-striped ledger-table">
                  <thead>
                    <tr>
                      <td colSpan={2}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={handleSearch}
                          />
                        </div>
                      </td>
                      <td colSpan={3} style={{ textAlign: 'right' }}>
                        <ExportButtons
                          data={filteredCategories}
                          filename="categories"
                          title="Categories Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'cat_name', label: 'Category Name' },
                            { key: 'description', label: 'Description' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCategories.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No categories found
                        </td>
                      </tr>
                    ) : (
                      currentCategories.map((category) => (
                        <tr key={category.id}>
                          <td>{category.id}</td>
                          <td>{category.cat_name}</td>
                          <td>{category.description || 'N/A'}</td>
                          <td className="action-buttons">
                            <button
                              className="btn btn-info btn-xs"
                              title="Edit"
                              onClick={() => handleEdit(category)}
                            >
                              <i className="zmdi zmdi-edit"></i>
                            </button>
                            <button
                              className="btn btn-danger btn-xs ml-1"
                              title="Delete"
                              onClick={() => handleDelete(category)}
                            >
                              <i className="zmdi zmdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        return (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                  <div className="pagination-info text-center mt-2">
                    <small className="text-muted">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCategories.length)} of {filteredCategories.length} entries
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={closeModal}
          title="Edit Category"
        >
          <form onSubmit={handleUpdate}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Category Name *
              </label>
              <input
                type="text"
                name="cat_name"
                value={editFormData.cat_name || ''}
                onChange={handleEditInputChange}
                placeholder="Enter category name"
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description
              </label>
              <textarea
                name="description"
                value={editFormData.description || ''}
                onChange={handleEditInputChange}
                placeholder="Enter category description"
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                type="button" 
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  backgroundColor: loading ? '#ccc' : '#007bff',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Updating...' : 'Update Category'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </Layout>
  );
};

export default Categories;
